import React, { useEffect, useMemo, useRef } from 'react'
import { useHospitalSelection } from '../../hooks/useHospitalSelection'
import { useHospitalData } from '../../hooks/useHospitalData'
import { usePersistedState } from '../../hooks/usePersistedState'
import DateRangeSlider from '../../components/DateRangeSlider/DateRangeSlider'
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice'
import NoDataOverlay from '../../components/Common/NoDataOverlay'
import { parseMonthToValue } from '../../utils/dataUtils'
import { MonthInfo, generateMonthList } from '../../utils/coreUtils'
import {
    createChartExportHandlers,
    createMenuToggle,
    setupMenuClickOutside,
    exportToCSV,
} from '../../utils/exportUtils'
import './ClinicalPracticeObservations.css'

interface ZohoResponse {
    response?: {
        result?: {
            column_order?: string[]
            rows?: any[][]
        }
    }
}

interface HospitalComments {
    hospitalId: string
    hospitalName: string
    comments: string[]
}

const SECTION_MENU_ID = 'hdl-clinical-practice-export-menu'
const HDL_SLIDER_KEY = 'hdl_slider'
const QUESTION = 'Q16: What challenges did you observe or experience?'

const getSliderIndices = (monthList: MonthInfo[], range: [number, number]): [number, number] => {
    if (!monthList.length) return [0, 0]
    const maxIndex = monthList.length - 1
    const [startVal, endVal] = range
    if (!startVal || !endVal) return [0, maxIndex]

    let startIdx = monthList.findIndex(m => m.value === startVal)
    if (startIdx === -1) startIdx = monthList.findIndex(m => m.value >= startVal)
    if (startIdx === -1) startIdx = 0

    let endIdx = -1
    for (let i = maxIndex; i >= 0; i--) {
        if (monthList[i].value <= endVal) { endIdx = i; break }
    }
    if (endIdx === -1) endIdx = maxIndex

    startIdx = Math.max(0, Math.min(startIdx, maxIndex))
    endIdx = Math.max(0, Math.min(endIdx, maxIndex))
    if (startIdx > endIdx) return [0, maxIndex]
    return [startIdx, endIdx]
}

const ClinicalPracticeObservations: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect } = useHospitalSelection()

    const hospitalIds = useMemo(
        () => (Array.isArray(selectedHospitalIds) ? selectedHospitalIds : []),
        [selectedHospitalIds]
    )

    const { data: commentsData, loading, initialLoading, error } = useHospitalData<ZohoResponse>(
        '/api/hdl/clinical_practice_q16',
        hospitalIds
    )

    const sectionRef = useRef<HTMLDivElement>(null)
    const sectionExports = useMemo(
        () => createChartExportHandlers(
            sectionRef,
            'Clinical Practice Observations',
            'clinical_practice_observations_q16',
            { fontBoost: true, exportTitle: 'Clinical Practice Observations', exportTitleAlign: 'center', exportTitleFontSize: '28px' }
        ),
        []
    )
    const toggleSectionMenu = useMemo(() => createMenuToggle(SECTION_MENU_ID), [])

    useEffect(() => {
        return setupMenuClickOutside(() => sectionExports.closeMenu(SECTION_MENU_ID))
    }, [sectionExports])

    const rows: any[][] = useMemo(
        () => commentsData?.response?.result?.rows || [],
        [commentsData]
    )

    const colIdx = useMemo(() => {
        const columnOrder: string[] = commentsData?.response?.result?.column_order || []
        const find = (...keys: string[]) => {
            for (const key of keys) {
                const i = columnOrder.findIndex(c => c.toLowerCase() === key.toLowerCase())
                if (i !== -1) return i
            }
            return -1
        }
        const idx = (keys: string[], fallback: number) => {
            const found = find(...keys)
            return found !== -1 ? found : fallback
        }
        return {
            hospId:   idx(['hospital_id', 'hospitalid'], 0),
            hospName: idx(['hospital_name', 'hospitalname'], 1),
            month:    idx(['month', 'month_year', 'date', 'submission_date'], 2),
            comment:  idx(['comment', 'comments', 'clinical_practice', 'response', 'q16_response', 'q16_comment', 'answer'], 3),
        }
    }, [commentsData])

    const monthList: MonthInfo[] = useMemo(() => {
        if (!rows.length) return []
        return generateMonthList(rows, colIdx.month)
    }, [rows, colIdx])

    const hospitalIdsKey = hospitalIds.join('_')
    const persistenceKey = `${HDL_SLIDER_KEY}_${hospitalIdsKey}`
    const [sliderRangeValues, setSliderRangeValues] = usePersistedState<[number, number]>(
        persistenceKey,
        [0, 0]
    )

    const userInteractedRef = useRef(false)
    const initSeenRef = useRef(false)
    const lastDetectedKeyRef = useRef<string | null>(null)

    if (hospitalIds.length > 0 && lastDetectedKeyRef.current !== persistenceKey) {
        const isFirstInit = !initSeenRef.current
        lastDetectedKeyRef.current = persistenceKey
        initSeenRef.current = true

        if (isFirstInit) {
            try {
                const item = sessionStorage.getItem(persistenceKey)
                const parsed = item !== null ? JSON.parse(item) : null
                userInteractedRef.current =
                    Array.isArray(parsed) && parsed[0] !== 0 && parsed[1] !== 0
            } catch {
                userInteractedRef.current = false
            }
        } else {
            userInteractedRef.current = false
        }
    }

    useEffect(() => {
        if (!monthList.length) return
        if (loading) return
        const maxIdx = monthList.length - 1
        const fullRange: [number, number] = [monthList[0].value, monthList[maxIdx].value]
        const [curStart, curEnd] = sliderRangeValues

        if (!userInteractedRef.current) {
            if (curStart !== fullRange[0] || curEnd !== fullRange[1]) {
                setSliderRangeValues(fullRange)
            }
            return
        }

        const stillValid =
            curStart >= monthList[0].value &&
            curEnd <= monthList[maxIdx].value &&
            curStart <= curEnd
        if (!curStart || !curEnd || !stillValid) {
            userInteractedRef.current = false
            setSliderRangeValues(fullRange)
        }
    }, [hospitalIdsKey, monthList, loading, sliderRangeValues, setSliderRangeValues])

    const [startIdx, endIdx] = useMemo(
        () => getSliderIndices(monthList, sliderRangeValues),
        [monthList, sliderRangeValues]
    )

    const filteredRows = useMemo(() => {
        if (!monthList.length) return []
        const minVal = monthList[startIdx]?.value ?? 0
        const maxVal = monthList[endIdx]?.value ?? 0
        return rows.filter(r => {
            const monthVal = parseMonthToValue(r[colIdx.month])
            return monthVal && monthVal >= minVal && monthVal <= maxVal
        })
    }, [rows, monthList, startIdx, endIdx, colIdx])

    const commentsByHospital = useMemo((): HospitalComments[] => {
        const map = new Map<string, { name: string; comments: string[] }>()

        filteredRows.forEach(row => {
            const hospId = String(row[colIdx.hospId] || '').trim() || hospitalIds[0] || ''
            const hospName = String(row[colIdx.hospName] || '').trim()
            const raw = row[colIdx.comment]

            const rawStr = raw === null || raw === undefined ? '' : String(raw).trim()
            const isBlank =
                rawStr === '' ||
                rawStr.toLowerCase() === '(blank)' ||
                rawStr.toLowerCase() === 'null' ||
                rawStr === '-'
            const comment = isBlank ? 'Null' : rawStr

            if (!hospId) return
            if (!map.has(hospId)) map.set(hospId, { name: hospName || hospId, comments: [] })
            map.get(hospId)!.comments.push(comment)
        })

        if (isMultiSelect && map.size > 1) {
            const allComments: string[] = []
            map.forEach(d => allComments.push(...d.comments))
            return [{ hospitalId: 'pooled', hospitalName: 'All Selected Hospitals', comments: allComments }]
        }

        return Array.from(map.entries())
            .map(([id, d]) => ({ hospitalId: id, hospitalName: d.name, comments: d.comments }))
            .sort((a, b) => a.hospitalId.localeCompare(b.hospitalId))
    }, [filteredRows, isMultiSelect, colIdx])

    const handleDownloadCSV = () => {
        const startLabel = monthList[startIdx]?.label || ''
        const endLabel = monthList[endIdx]?.label || ''
        const monthRange = startLabel && endLabel ? `${startLabel} - ${endLabel}` : ''
        const hospitalIdLabel = hospitalIds.join(', ')

        const csvRows: (string | number)[][] = commentsByHospital.flatMap(hospital =>
            hospital.comments.map(c => [
                hospital.hospitalId === 'pooled' ? hospitalIdLabel : hospital.hospitalId,
                monthRange,
                QUESTION,
                c,
            ])
        )

        exportToCSV({
            headers: ['Hospital Id', 'Date Range', 'Question', 'Comment'],
            rows: csvRows,
            filename: 'clinical_practice_observations_q16',
        })
    }

    const showInitialLoading = initialLoading && loading
    const isRefreshing = !initialLoading && loading
    const hasHospitals = hospitalIds.length > 0
    const noData = !initialLoading && !loading && !monthList.length

    const stableDataRef = useRef<HospitalComments[]>([])
    const confirmedKeyRef = useRef<string>('')
    const wasLoadingRef = useRef(false)

    const loadingJustFinished = wasLoadingRef.current && !loading
    wasLoadingRef.current = loading

    if (loadingJustFinished) {
        if (commentsByHospital.length > 0) stableDataRef.current = commentsByHospital
        confirmedKeyRef.current = hospitalIdsKey
    }

    const displayedData =
        (isRefreshing || confirmedKeyRef.current !== hospitalIdsKey) && stableDataRef.current.length > 0
            ? stableDataRef.current
            : commentsByHospital

    const hasData = displayedData.some(h => h.comments.some(c => c !== 'Null'))

    return (
        <div className="hdl-clinical-practice">
            <div className="hdl-page-header">
                <h1 className="hdl-page-title">Clinical Practice Observations</h1>
                <div className="hdl-slider-wrap">
                    {monthList.length > 0 && (
                        <DateRangeSlider
                            monthList={monthList}
                            startIndex={startIdx}
                            endIndex={endIdx}
                            onChange={(s, e) => {
                                const sv = monthList[s]?.value || 0
                                const ev = monthList[e]?.value || 0
                                if (sv && ev) {
                                    userInteractedRef.current = true
                                    setSliderRangeValues([sv, ev])
                                }
                            }}
                        />
                    )}
                </div>
            </div>

            <div className={`hdl-card${hasHospitals && !error && noData ? ' hdl-card-empty' : ''}`}>
                {showInitialLoading && <LoadingNotice title="Loading observations…" />}

                {!hasHospitals && (
                    <div className="hdl-notice">Select at least one hospital to view data.</div>
                )}

                {hasHospitals && error && (
                    <div className="hdl-notice hdl-notice-error">Failed to load data: {error}</div>
                )}

                <NoDataOverlay visible={hasHospitals && !error && noData} />

                {hasHospitals && !error && !noData && (
                    <div className="hdl-cp-section" ref={sectionRef}>
                        {hasData ? (
                            <>
                                <div className="hdl-cp-question-row">
                                    <span className="hdl-cp-question">{QUESTION}</span>
                                    <div className="export-menu export-ignore">
                                        <button className="hamburger" onClick={toggleSectionMenu}>&#9776;</button>
                                        <div id={SECTION_MENU_ID} className="export-dropdown">
                                            <div onClick={() => { handleDownloadCSV(); sectionExports.closeMenu(SECTION_MENU_ID) }}>Download CSV</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="hdl-cp-table-wrap">
                                    {displayedData.map((hospital, hospIdx) => (
                                        <React.Fragment key={hospIdx}>
                                            {displayedData.length > 1 && (
                                                <div className="hdl-obs-hospital-label">
                                                    {hospital.hospitalName || hospital.hospitalId}
                                                </div>
                                            )}
                                            <table className="hdl-obs-comments-table">
                                                <tbody>
                                                    {hospital.comments.map((comment, idx) => (
                                                        <tr
                                                            key={idx}
                                                            className={idx % 2 === 0 ? 'hdl-obs-row-even' : 'hdl-obs-row-odd'}
                                                        >
                                                            <td className={`hdl-obs-comment-cell${comment === 'Null' ? ' hdl-obs-comment-null' : ''}`}>
                                                                {comment}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </React.Fragment>
                                    ))}
                                </div>
                            </>
                        ) : (
                            <div className="hdl-cp-empty">No comments to display</div>
                        )}

                        {isRefreshing && (
                            <div className="chart-loading-overlay">
                                <div>Loading...</div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default ClinicalPracticeObservations
